// StudioCard — one source, material or other item on the Studio canvas (pen
// "Kort", "Studio – detail design" §2), pure. The head (type icon, the kind
// in caps — a material's template, "Source · PDF", "Item" for any other
// kind — and the AI mark), the title (serif, two lines, then an ellipsis; the
// full title is the tooltip), the preview's place, and one meta line: how far
// a source has been read, what a material was made from.
//
// The card's body only; where it sits, selection and dragging belong to the
// canvas (components/Studio).

export { StudioCard };

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { SourceReadStatus } from "../SourceReadStatus/SourceReadStatus.js";
import type {
    GenericCard,
    MaterialCard,
    SourceCard,
} from "../Studio/studioCards.js";
import "./StudioCard.css";

type StudioCardProps = {
    readonly card: SourceCard | MaterialCard | GenericCard;
};

const ICON: Record<StudioCardProps["card"]["type"], IconName> = {
    source: "source",
    material: "material",
    generic: "file",
};

const StudioCard = ({ card }: StudioCardProps): ReactNode => {
    const { t } = useLingui();
    const eyebrow = ((): readonly string[] => {
        switch (card.type) {
            case "source":
                return [t`Source`, card.format];
            // The kind the author knows it by: its template, else Material.
            case "material":
                return [
                    card.template === "quiz"
                        ? t`Quiz`
                        : card.template === "question"
                          ? t`Question`
                          : t`Material`,
                ];
            // Any other or unknown kind (the default): a generic item.
            case "generic":
                return [t`Item`, card.format];
        }
    })()
        .filter((s) => s !== "")
        .join(" · ");
    const status = ((): ReactNode => {
        switch (card.type) {
            case "source":
                return card.readStatus?.type === "source_read_ready" ? (
                    t`Read`
                ) : (
                    <SourceReadStatus status={card.readStatus} />
                );
            case "material": {
                if (card.planned) return t`Queued`;
                const [first, ...others] = card.basedOn;
                const from =
                    first === undefined
                        ? ""
                        : others.length === 0
                          ? t`From ${first}`
                          : t`From ${first} +${others.length}`;
                const versions =
                    card.versions === undefined || card.versions < 2
                        ? ""
                        : t`${plural(card.versions, {
                              one: "# version",
                              other: "# versions",
                          })}`;
                return [from, versions].filter((s) => s !== "").join(" · ");
            }
            case "generic":
                return null;
        }
    })();
    return (
        <div
            className="bilda-studio-card"
            data-kind={card.type}
            data-planned={
                card.type === "material" && card.planned ? "true" : undefined
            }
        >
            <div className="bilda-studio-card__eyebrow">
                <Icon name={ICON[card.type]} size="xs" />
                <span className="bilda-studio-card__kind">{eyebrow}</span>
                {card.type === "material" && card.aiAuthored ? (
                    <AiMarker variant="chip" />
                ) : null}
            </div>
            <p className="bilda-studio-card__title" title={card.title}>
                {card.title}
            </p>
            {/* The preview's place (a page's lines, drawn; no thumbnail yet). */}
            <div className="bilda-studio-card__preview" aria-hidden="true">
                <span />
                <span />
                <span />
            </div>
            {status === null || status === "" ? null : (
                <div className="bilda-studio-card__status">{status}</div>
            )}
        </div>
    );
};
